import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import type { DataNode, EventDataNode } from 'antd/es/tree';

import { useAuthorization } from '@/shared/hooks/useAuthorization';
import { NOTIFICATION_MESSAGES, ROUTES, TREE_DEPTH } from '@/shared/lib/constants';
import { calculateFqnDepth, getParentFqn, parseFqnToPathParts, removeCategoryPrefix } from '@/shared/lib/format/fqn';
import { glossaryKeys } from '@/shared/lib/query/queryKeys';
import type { TreeNode } from '@/shared/lib/tree';
import { CategoryTree } from '@/shared/ui/CategoryTree';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';

import type { GlossaryCategoryItem, GlossaryTermItem } from '@/entities/glossary';

import { useGlossaryCategory } from '@/features/glossary/model/useGlossaryCategory';
import { useGlossaryTerm } from '@/features/glossary/model/useGlossaryTerm';

import styles from './GlossaryCategoryTree.module.css';

const NAVIGATION_DEPTH = 0;

const transformCategoryToTreeNode = (categories: GlossaryCategoryItem[]): TreeNode[] => {
  if (categories.length === 0) return [];

  const treeNodes = categories.map((category) => {
    const depth = calculateFqnDepth(category.fullyQualifiedName);

    const hasNoTerms = !category.termCount;
    const isMaxDepth = depth >= TREE_DEPTH.GLOSSARY_CATEGORY;
    const isLeaf = hasNoTerms || isMaxDepth;

    return {
      key: category.fullyQualifiedName,
      title: category.displayName || category.name,
      count: category.termCount,
      isLeaf,
    };
  });
  return treeNodes;
};

const transformTermToTreeNode = (terms: GlossaryTermItem[]): TreeNode[] => {
  return terms.map((term) => {
    const depth = calculateFqnDepth(term.fullyQualifiedName);

    const hasNoChildren = !term.childrenCount;
    const isMaxDepth = depth >= TREE_DEPTH.GLOSSARY_CATEGORY;
    const isLeaf = hasNoChildren || isMaxDepth;

    return {
      key: term.fullyQualifiedName,
      title: term.displayName || term.name,
      count: term.childrenCount,
      isLeaf,
    };
  });
};

type GlossaryCategoryTreeProps = {
  selectedTermFqn?: string;
  onAddClick?: () => void;
  refreshParentFqn?: string | null;
  onRefreshComplete?: () => void;
};

const Tree = ({ selectedTermFqn, onAddClick, refreshParentFqn, onRefreshComplete }: GlossaryCategoryTreeProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { termCategory } = useParams<{ termCategory?: string }>();
  const { hasAnyRole } = useAuthorization();

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [loadedChildren, setLoadedChildren] = useState<Map<string, TreeNode[]>>(new Map());
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingLoadKey, setPendingLoadKey] = useState<string | null>(null);

  const prevRefreshParentFqnRef = useRef<string | null | undefined>(undefined);

  const canManageCategories = hasAnyRole(['admin', 'manager']);

  const { data: categoryData, isLoading: isCategoryLoading } = useGlossaryCategory({
    limit: 3000,
  });
  const { data: termData, isLoading: isTermLoading } = useGlossaryTerm({
    directChildrenOf: pendingLoadKey || '',
  });

  const getChildrenToNode = useCallback(
    (node: TreeNode): TreeNode => {
      const cachedChildren = loadedChildren.get(node.key);

      if (!cachedChildren) return node;
      return {
        ...node,
        children: cachedChildren.map((child) => getChildrenToNode(child)),
      };
    },
    [loadedChildren]
  );

  const treeData = useMemo(() => {
    if (!categoryData) return [];
    const baseTreeData = transformCategoryToTreeNode(categoryData);
    return baseTreeData.map((node) => getChildrenToNode(node));
  }, [categoryData, getChildrenToNode]);

  const onCollapseAll = useCallback(() => {
    if (expandedKeys.length === 0) return;
    setExpandedKeys([]);
  }, [expandedKeys.length]);

  const onSelect = useCallback(
    (
      newSelectedKeys: React.Key[],
      info: {
        event: 'select';
        selected: boolean;
        node: EventDataNode<DataNode>;
        selectedNodes: DataNode[];
        nativeEvent: MouseEvent;
      }
    ) => {
      if (!info.selected || newSelectedKeys.length === 0) {
        setSelectedKeys([]);
        return;
      }

      const selectedKey = newSelectedKeys[0] as string;
      const depth = calculateFqnDepth(selectedKey);

      setSelectedKeys(newSelectedKeys);

      if (depth === NAVIGATION_DEPTH) {
        navigate(`${ROUTES.GLOSSARY}/${selectedKey}`);
      } else {
        const categoryFqn = parseFqnToPathParts(selectedKey)[0];
        const termPath = removeCategoryPrefix(selectedKey);
        navigate(`${ROUTES.GLOSSARY}/${categoryFqn}/${termPath}`);
      }

      if (depth >= TREE_DEPTH.GLOSSARY_CATEGORY) return;
      if (!expandedKeys.includes(selectedKey)) {
        setExpandedKeys([...expandedKeys, selectedKey]);

        if (!loadedChildren.has(selectedKey) && !pendingLoadKey) {
          setPendingLoadKey(selectedKey);
        }
      }
    },
    [expandedKeys, loadedChildren, pendingLoadKey, navigate]
  );

  const onExpand = useCallback(
    (newExpandedKeys: React.Key[], info: { expanded: boolean; node: EventDataNode<DataNode> }) => {
      const filteredExpandedKeys = newExpandedKeys.filter((key) => {
        const depth = calculateFqnDepth(key as string);
        const isMaxDepth = depth >= TREE_DEPTH.GLOSSARY_CATEGORY;
        return !isMaxDepth;
      });

      setExpandedKeys(filteredExpandedKeys);

      if (info.expanded && info.node && info.node.key) {
        const expandedKey = info.node.key as string;
        const depth = calculateFqnDepth(expandedKey);

        if (depth < TREE_DEPTH.GLOSSARY_CATEGORY) {
          if (!loadedChildren.has(expandedKey)) {
            setPendingLoadKey(expandedKey);
          }
        }
      }
    },
    [loadedChildren]
  );

  const loadingKeys = isTermLoading && pendingLoadKey ? [pendingLoadKey] : [];

  useEffect(() => {
    if (!pendingLoadKey || !termData?.data) return;

    const depth = calculateFqnDepth(pendingLoadKey);
    if (depth >= TREE_DEPTH.GLOSSARY_CATEGORY) return;

    const children = transformTermToTreeNode(termData.data);
    setLoadedChildren((prev) => {
      const newMap = new Map(prev);
      newMap.set(pendingLoadKey, children);
      return newMap;
    });

    setPendingLoadKey(null);
  }, [pendingLoadKey, termData]);

  useEffect(() => {
    if (!refreshParentFqn || refreshParentFqn === prevRefreshParentFqnRef.current) return;

    prevRefreshParentFqnRef.current = refreshParentFqn;

    queryClient.removeQueries({ queryKey: glossaryKeys.terms(refreshParentFqn) });

    const grandParentFqn = getParentFqn(refreshParentFqn);
    if (grandParentFqn) {
      queryClient.removeQueries({ queryKey: glossaryKeys.terms(grandParentFqn) });

      setLoadedChildren((prev) => {
        const newMap = new Map(prev);
        newMap.delete(refreshParentFqn);
        newMap.delete(grandParentFqn);
        return newMap;
      });

      if (expandedKeys.includes(grandParentFqn) && pendingLoadKey === null) {
        setPendingLoadKey(grandParentFqn);
      } else if (expandedKeys.includes(refreshParentFqn) && pendingLoadKey === null) {
        setPendingLoadKey(refreshParentFqn);
      }
    } else {
      setLoadedChildren((prev) => {
        const newMap = new Map(prev);
        newMap.delete(refreshParentFqn);
        return newMap;
      });

      if (expandedKeys.includes(refreshParentFqn) && pendingLoadKey === null) {
        setPendingLoadKey(refreshParentFqn);
      }
    }

    prevRefreshParentFqnRef.current = null;
    onRefreshComplete?.();
  }, [refreshParentFqn, queryClient, expandedKeys, pendingLoadKey, onRefreshComplete]);

  useEffect(() => {
    if (pendingLoadKey !== null) return;

    for (const key of expandedKeys) {
      const keyStr = key as string;
      const depth = calculateFqnDepth(keyStr);
      if (depth < TREE_DEPTH.GLOSSARY_CATEGORY && !loadedChildren.has(keyStr)) {
        setPendingLoadKey(keyStr);
        break;
      }
    }
  }, [expandedKeys, loadedChildren, pendingLoadKey]);

  const prevSelectedTermFqnRef = useRef<string | undefined>(selectedTermFqn);

  useEffect(() => {
    if (selectedTermFqn !== prevSelectedTermFqnRef.current) {
      prevSelectedTermFqnRef.current = selectedTermFqn;

      if (selectedTermFqn) {
        setSelectedKeys([selectedTermFqn]);
      } else if (termCategory) {
        setSelectedKeys([termCategory]);
      }
    }
  }, [selectedTermFqn, termCategory]);

  useEffect(() => {
    if (!categoryData || categoryData.length === 0) return;
    if (!termCategory) return;

    if (selectedTermFqn) {
      setSelectedKeys([selectedTermFqn]);
      const parts = selectedTermFqn.split('.');
      const keysToExpand: string[] = [];
      for (let i = 1; i <= parts.length; i++) {
        const partialFqn = parts.slice(0, i).join('.');
        keysToExpand.push(partialFqn);
      }
      const filteredKeys = keysToExpand.filter((key) => calculateFqnDepth(key) < TREE_DEPTH.GLOSSARY_CATEGORY);

      setExpandedKeys((prevKeys) => {
        const newKeysSet = new Set(prevKeys);
        filteredKeys.forEach((key) => newKeysSet.add(key));
        return Array.from(newKeysSet);
      });

      const parentsToLoad = filteredKeys.filter((key) => !loadedChildren.has(key));
      if (parentsToLoad.length > 0 && !pendingLoadKey) {
        setPendingLoadKey(parentsToLoad[0]);
      }

      setIsInitialized(true);
      return;
    }

    if (termCategory && !isInitialized) {
      setSelectedKeys([termCategory]);
      setExpandedKeys([termCategory]);

      if (!loadedChildren.has(termCategory)) {
        setPendingLoadKey(termCategory);
      }

      setIsInitialized(true);
      return;
    }

    if (!isInitialized) {
      const firstCategory = categoryData[0];
      if (firstCategory) {
        const categoryFqn = firstCategory.fullyQualifiedName;
        setSelectedKeys([categoryFqn]);
        setExpandedKeys([categoryFqn]);

        if (!loadedChildren.has(categoryFqn)) {
          setPendingLoadKey(categoryFqn);
        }

        setIsInitialized(true);
        navigate(`${ROUTES.GLOSSARY}/${categoryFqn}`);
      }
    }
  }, [selectedTermFqn, termCategory, categoryData, isInitialized, loadedChildren, pendingLoadKey, navigate]);

  useEffect(() => {
    if (termCategory) return;
    if (!categoryData || categoryData.length === 0) return;

    const firstCategory = categoryData[0];
    if (firstCategory) {
      const categoryFqn = firstCategory.fullyQualifiedName;
      setSelectedKeys([categoryFqn]);
      setExpandedKeys([categoryFqn]);

      if (!loadedChildren.has(categoryFqn)) {
        setPendingLoadKey(categoryFqn);
      }

      navigate(`${ROUTES.GLOSSARY}/${categoryFqn}`);
    }
  }, [termCategory, categoryData, loadedChildren, pendingLoadKey, navigate]);

  return (
    <CategoryTree
      headerTitle="용어 카테고리"
      isLoading={isCategoryLoading}
      showCount={true}
      treeData={treeData}
      loadingKeys={loadingKeys}
      expandedKeys={expandedKeys}
      selectedKeys={selectedKeys}
      onCollapseAll={onCollapseAll}
      onSelect={onSelect}
      onExpand={onExpand}
      onAddClick={onAddClick}
      addButtonText="용어집 추가"
      showAddButton={canManageCategories}
    />
  );
};

const Error = (props: React.ComponentProps<typeof ErrorFallback>) => {
  return (
    <div className={styles.error}>
      <ErrorFallback
        {...props}
        title={NOTIFICATION_MESSAGES.GLOSSARY_CATEGORY_ERROR}
        description={NOTIFICATION_MESSAGES.GLOSSARY_CATEGORY_ERROR_DESC}
      />
    </div>
  );
};

export const GlossaryCategoryTree = { Tree, Error };
